/**
 * Metabase API Service
 * 
 * This service provides functions for interacting with the Metabase report API.
 * It handles all CRUD operations and includes comprehensive error handling.
 * 
 * API Endpoint: /v6/metabase
 * 
 * Error Handling:
 * - 400: Validation errors (field-specific errors returned)
 * - 404: Report not found
 * - 423: Report is locked and cannot be modified
 * - 500: Server error
 * - Network errors: Connection issues
 */

import { axios } from '../api';
import { AxiosError } from 'axios';
import {
  MetabaseReport,
  MetabaseReportParams,
  MetabaseReportCreate,
  MetabaseReportUpdate,
  ListResponse,
  ErrorResponse,
} from '../types/metabase';

const BASE_URL = '/v6/metabase';

/**
 * ApiError - Custom error class for API errors
 * Provides structured error information for UI display
 */
export class ApiError extends Error {
  status: number;
  details?: Record<string, string[]>;

  constructor(message: string, status: number, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

/**
 * handleApiError - Convert axios errors to structured ApiError
 * Extracts meaningful error messages and field-specific validation errors
 */
function handleApiError(error: unknown): never {
  if (error instanceof AxiosError) {
    const status = error.response?.status || 0;
    const data = error.response?.data as ErrorResponse | undefined;

    // Handle specific status codes
    switch (status) {
      case 400:
        throw new ApiError(
          data?.error || 'Validation error',
          400,
          data?.details
        );
      case 404:
        throw new ApiError('Report not found', 404);
      case 423:
        throw new ApiError('This report is locked and cannot be modified', 423);
      case 500:
        throw new ApiError(
          data?.error || 'Backend server error occurred while processing the request',
          500
        );
      default:
        // Network error or other error
        if (!error.response) {
          // Check if it's actually a server error disguised as network error
          if (error.message === 'Network Error' && error.code === 'ERR_NETWORK') {
            throw new ApiError(
              'Backend server error (500). The GET /v6/metabase/{id} endpoint is crashing.',
              500
            );
          }
          throw new ApiError(
            'Unable to connect to server. Please check your connection.',
            0
          );
        }
        throw new ApiError(
          data?.error || 'An unexpected error occurred',
          status
        );
    }
  }

  // Non-axios error
  throw new ApiError('An unexpected error occurred', 0);
}

/**
 * list - Fetch a paginated list of Metabase reports
 * 
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns Promise<ListResponse> - Paginated list of reports
 * 
 * Example:
 * ```
 * const response = await metabaseApi.list({
 *   q: 'dashboard',
 *   company_id: 123,
 *   page: 1,
 *   page_size: 25,
 *   sort_by: 'name',
 *   sort_order: 'asc'
 * });
 * ```
 */
export async function list(params?: MetabaseReportParams): Promise<ListResponse> {
  try {
    const response = await axios.get<ListResponse>(BASE_URL, { params });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * get - Fetch a single Metabase report by ID
 * 
 * @param id - Report ID
 * @returns Promise<MetabaseReport> - The report object
 * @throws ApiError with status 404 if report not found
 */
export async function get(id: number): Promise<MetabaseReport> {
  try {
    const response = await axios.get(`${BASE_URL}/${id}`);
    
    // Handle different response formats from MetabaseReportDetail model
    if (response.data && typeof response.data === 'object') {
      // If it's wrapped in a data property
      if ('data' in response.data) {
        return response.data.data;
      }
      // If it's returned directly
      return response.data;
    }
    
    throw new Error('Invalid response format');
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * create - Create a new Metabase report
 * 
 * @param data - Report data (all required fields must be provided)
 * @returns Promise<MetabaseReport> - The created report object
 * @throws ApiError with status 400 if validation fails
 */
export async function create(data: MetabaseReportCreate): Promise<MetabaseReport> {
  try {
    const response = await axios.post<MetabaseReport>(BASE_URL, data);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * update - Update an existing Metabase report
 * 
 * @param id - Report ID
 * @param data - Partial report data (only fields to update)
 * @returns Promise<MetabaseReport> - The updated report object
 * @throws ApiError with status 404 if report not found
 * @throws ApiError with status 423 if report is locked
 * @throws ApiError with status 400 if validation fails
 * 
 * Example:
 * ```
 * const updatedReport = await metabaseApi.update(123, {
 *   title: 'Updated Title',
 *   priority: 2
 * });
 * ```
 */
export async function update(
  id: number,
  data: MetabaseReportUpdate
): Promise<MetabaseReport> {
  try {
    const response = await axios.put<MetabaseReport>(`${BASE_URL}/${id}`, data);
    // Check if the response is wrapped in a data property or returned directly
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return (response.data as any).data;
    }
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * deleteReport - Delete a Metabase report (soft delete - sets active to false)
 * 
 * @param id - Report ID
 * @returns Promise<void>
 * @throws ApiError with status 404 if report not found
 * @throws ApiError with status 423 if report is locked
 * 
 * Example:
 * ```
 * await metabaseApi.deleteReport(123);
 * ```
 */
export async function deleteReport(id: number): Promise<void> {
  try {
    await axios.delete(`${BASE_URL}/${id}`);
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * metabaseApi - Exported API object with all functions
 * Provides a convenient namespace for all Metabase API operations
 */
export const metabaseApi = {
  list,
  get,
  create,
  update,
  delete: deleteReport,
};
